import { animate, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  signal,
  Signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { CustomUserDropdownItem, IdentifiableCustomUserDropdownItem } from '@galaxy/atlas';
import { SessionService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import type { DropdownItem, PinnedItem, SideNavigationItem, SideNavigationLink } from '@vendasta/protos/atlas/v1/types';
import { GalaxyNavControlService } from '@vendasta/galaxy/nav';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, startWith, tap } from 'rxjs/operators';
import { BusinessNavConfigService } from '../config.service';
import { SidebarService } from '../core-components/sidebar/sidebar.service';
import { BusinessNavDataService } from '../data.service';
import { BusinessNavHostBridge, isAbsoluteHttpUrl } from '../host-bridge';
import { flattenNavigationLinks } from '../navigation-links';
import { SalesInfoModalComponent } from '../modals/sales-info/sales-info.modal.component';
import { PinsService } from '../pins.service';
import { isDarkTheme, StyleService } from '@vendasta/galaxy/style-service';
import { AUTO_HIDE_NAV_MAX_WIDTH_DEFAULT } from '@vendasta/galaxy/nav/src/constants';
import { signOutLabel, stopImpersonatingLabel } from '../globals';

export const BUSINESS_APP_SERVICE_PROVIDER_ID = 'VBC';

type themingValues = {
  logoUrl: string;
  isDarkMode: boolean;
};

@Component({
  selector: 'bc-business-navbar',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  animations: [
    trigger('animateNav', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('0.15s ease-in-out', style({ transform: 'translateX(0%)' })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0%)' }),
        animate('0.15s ease-in-out', style({ transform: 'translateX(-100%)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NavigationComponent implements OnInit, OnChanges {
  @Input({ transform: booleanAttribute }) hideCenters: boolean;
  @Input({ transform: booleanAttribute }) hideNotifications: boolean;
  readonly switchToAdminUrl = input<string | null>(null);
  disableNavigation = input(false, { transform: booleanAttribute });
  @Input({ transform: booleanAttribute }) alwaysShowMenuToggle: boolean;
  @Input({ transform: booleanAttribute }) showLanguageSelector = true;
  protected readonly portal = viewChild(CdkPortal);

  readonly partnerId = input<string>();
  readonly accountId = input<string>();
  readonly marketId = input<string>();
  readonly brandPath = input<string>();
  readonly serviceProviderId = input<string>();
  readonly defaultURL = input<string>();
  readonly entitlementId = input<string>();
  readonly isPrimaryNav = input(false, { transform: booleanAttribute });
  readonly overrideSignOutText = input<string>();
  readonly hideLocationSwitcher = input(false, { transform: booleanAttribute });
  readonly extraDropdownItems = input<CustomUserDropdownItem[]>([]);

  private readonly extraDropdownItems$ = toObservable(this.extraDropdownItems);

  private readonly sidebarService = inject(SidebarService);
  private readonly cfgService = inject(BusinessNavConfigService);
  private readonly apiService = inject(BusinessNavDataService);
  private readonly translate = inject(TranslateService);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly dialog = inject(MatDialog);
  private readonly pinsService = inject(PinsService);
  private readonly overlay = inject(Overlay);
  private readonly navControlService = inject(GalaxyNavControlService);
  private readonly styleService = inject(StyleService);
  private readonly sessionService = inject(SessionService);
  private readonly bridge = inject(BusinessNavHostBridge);
  protected readonly external = this.bridge.isExternal;
  private readonly signOutHidden$ = toObservable(this.bridge.signOutHidden);

  protected readonly overlayDisabled = signal(false);
  protected readonly showLegacyButton: Signal<boolean>;
  protected readonly showNavToggle: Signal<boolean>;
  protected dropdownItems$: Observable<CustomUserDropdownItem[]>;

  protected readonly businessAppBranding$ = this.apiService.businessAppBranding$;
  protected readonly coBrandingUrl$ = this.apiService.cobrandingLogoURL$.pipe(map((url) => url || null));
  protected readonly branding$: Observable<themingValues>;
  protected readonly businessCenterName$ = this.apiService.businessCenterName$;
  protected readonly salespersonPictureUrl$ = this.apiService.salespersonPictureUrl$;
  protected readonly partnerName$ = this.apiService.partnerName$;
  protected readonly salespersonName$ = this.apiService.salespersonName$;
  protected readonly exitLinkConfiguration$ = this.apiService.exitLinkConfiguration$;
  protected readonly disableProductSwitcher$ = this.apiService.disableProductSwitcher$;

  protected readonly canShowContactCard$ = combineLatest([
    this.apiService.contactUsModalDescription$,
    this.apiService.hasSalesperson$,
    this.apiService.hideSalespersonContact$,
    this.apiService.canAccessInbox$,
    this.apiService.showSupportCenterLink$,
    this.apiService.supportCenterLinkUrl$,
  ]).pipe(
    map(([description, hasSalesperson, hideSalesperson, canAccessInbox, showSupport, supportUrl]) =>
      computeCanShowContactCard(description, hasSalesperson, hideSalesperson, canAccessInbox, showSupport, supportUrl),
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  protected readonly navigationItems$ = combineLatest([
    this.apiService.navigationItems$,
    this.pinsService.pins$,
    this.cfgService.accountGroupId$,
  ]).pipe(map(([items, pins, agid]) => overrideWpHostingLink(removeUnpinnedItems(items, pins), agid)));

  protected readonly isMobile = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: ' + AUTO_HIDE_NAV_MAX_WIDTH_DEFAULT + 'px)')
      .pipe(map((result) => result.matches)),
  );

  private readonly logoHeight$$ = new ReplaySubject<number>(1);
  protected readonly logoHeight$: Observable<number>;

  private readonly overlayRef: Signal<OverlayRef>;
  private showMenuToggle = toSignal(this.sidebarService.showMenuToggle$);

  protected readonly navIsOpen = this.navControlService.isOpen;

  constructor() {
    effect(() => {
      if (this.serviceProviderId() === BUSINESS_APP_SERVICE_PROVIDER_ID && !this.external()) {
        document.body.classList.add('new-nav');
      }
    });

    effect(() => {
      if (this.external()) {
        this.bridge.drawerState.set({ open: this.navIsOpen(), overlay: this.isMobile() === true });
      }
    });

    this.logoHeight$ = this.logoHeight$$.asObservable().pipe(
      tap((height: number) => {
        this.rememberLastLogoHeight(height);
      }),
      startWith(this.getLastLogoHeight()),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.showNavToggle = computed(() => {
      if (this.disableNavigation()) return false;
      return (this.showMenuToggle() && this.isMobile()) || !this.navIsOpen();
    });

    // Init the nav
    this.calcAlwaysShowMenuToggle();
    this.matIconRegistry.addSvgIconLiteral(
      'store_outlined',
      this.domSanitizer.bypassSecurityTrustHtml(
        `<svg><path d="M18.36 9l.6 3H5.04l.6-3h12.72M20 4H4v2h16V4zm0 3H4l-1 5v2h1v6h10v-6h4v6h2v-6h1v-2l-1-5zM6 18v-4h6v4H6z"/></svg>`,
      ),
    );

    effect(() => {
      this.refreshConfig(
        this.partnerId(),
        this.brandPath(),
        this.marketId(),
        this.accountId(),
        this.serviceProviderId(),
        this.defaultURL(),
        this.entitlementId(),
      );
    });

    effect(() => {
      this.sidebarService.setDisabled(this.disableNavigation() === true);
    });

    this.showLegacyButton = computed(() => this.overlayDisabled() === false);

    combineLatest([this.cfgService.serviceProviderId$, this.apiService.disableBusinessNav$])
      .pipe(takeUntilDestroyed())
      .subscribe(([serviceProviderId, disableBusNav]) => {
        this.overlayDisabled.set(
          serviceProviderId === BUSINESS_APP_SERVICE_PROVIDER_ID ||
            (serviceProviderId !== BUSINESS_APP_SERVICE_PROVIDER_ID && disableBusNav),
        );
      });

    this.overlayRef = computed(() => {
      const isPortal = !!this.portal();
      const isPrimaryNav = this.isPrimaryNav();

      if (isPortal === true && isPrimaryNav === false) {
        const ovrRef = this.createOverlay();
        return ovrRef;
      }
    });

    this.branding$ = combineLatest([
      this.apiService.marketLogoUrl$,
      this.apiService.darkMarketLogoUrl$,
      this.styleService.activeTheme$,
    ]).pipe(
      map(([lightLogoUrl, darkLogoUrl, theme]) => {
        const displayDarkLogo = isDarkTheme(theme) && !!darkLogoUrl;
        if (!this.isPrimaryNav()) {
          return {
            isDarkMode: false,
            logoUrl: lightLogoUrl,
          };
        }
        return {
          isDarkMode: displayDarkLogo,
          logoUrl: displayDarkLogo ? darkLogoUrl : lightLogoUrl || '',
        };
      }),
      startWith({ isDarkMode: false, logoUrl: '' }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private createOverlay(): OverlayRef {
    const config: OverlayConfig = new OverlayConfig({
      positionStrategy: this.overlay.position().global().bottom().left().start(),
      direction: 'ltr',
      hasBackdrop: true,
      height: `100%`,
      width: '276px',
    });
    const overlayRef = this.overlay.create(config);
    overlayRef.backdropClick().subscribe(() => {
      overlayRef.detach();
    });
    return overlayRef;
  }

  ngOnInit(): void {
    const serverItems$ = combineLatest([
      this.apiService.dropdownItems$,
      this.translate.onTranslationChange.pipe(startWith(null)),
      this.signOutHidden$,
    ]).pipe(
      map(([dropdownItems, , signOutHidden]) =>
        dropdownItems.filter((item: DropdownItem) => !(signOutHidden && item.label === signOutLabel))
        .map((item: DropdownItem) => {
          let label: string = this.translate.instant(item.translationId || item.label);

          if (!label && item.translationId == 'NAVIGATION.MENU.EDIT_PROFILE') {
            label = item.label || 'Edit Profile';
          }

          const i: IdentifiableCustomUserDropdownItem = {
            id: item.label,
            label: label,
          };

          // Certain routes in VBC do not use the full URL
          const routesUsingFullURL =
            item.label == signOutLabel ||
            item.label == stopImpersonatingLabel ||
            (item.translationId == 'NAVIGATION.MENU.EDIT_PROFILE' && !item.path);
          if (
            this.serviceProviderId().toUpperCase() === BUSINESS_APP_SERVICE_PROVIDER_ID &&
            !routesUsingFullURL &&
            !this.external()
          ) {
            i.route = item.path;
          } else {
            i.url = item.url;
          }

          decorateSignOutItem(
            i,
            Capacitor.isNativePlatform(),
            () => this.sessionService.clearSessionId(),
            (url) => (window.location.href = url),
            (url) => this.bridge.signOut(url),
          );

          return i;
        }),
      ),
    );

    this.dropdownItems$ = combineLatest([serverItems$, this.extraDropdownItems$]).pipe(
      map(([serverItems, extraItems]) => {
        if (!extraItems.length) return serverItems;
        const signOutIndex = serverItems.findIndex(
          (item) => (item as IdentifiableCustomUserDropdownItem).id === signOutLabel,
        );
        if (signOutIndex === -1) return [...serverItems, ...extraItems];
        return [...serverItems.slice(0, signOutIndex), ...extraItems, ...serverItems.slice(signOutIndex)];
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.alwaysShowMenuToggle) {
      this.calcAlwaysShowMenuToggle();
    }
  }

  protected logoLoaded(logo: { height: number }): void {
    window.setTimeout(() => {
      this.logoHeight$$.next(Math.min(logo.height, 120));
    }, 0);
  }

  protected openAssistanceModal(): void {
    this.dialog.open(SalesInfoModalComponent, {
      width: '550px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
    });
  }

  private rememberLastLogoHeight(height: number): void {
    localStorage.setItem('business-sidenav-logo-height', '' + height);
  }

  private getLastLogoHeight(): number {
    const t = localStorage.getItem('business-sidenav-logo-height');
    if (!t) {
      return 80;
    }
    return +t;
  }

  protected toggleMenu(): void {
    if (this.isPrimaryNav()) {
      this.navControlService.toggle();
    } else {
      if (!this.overlayRef() || !this.portal() || this.overlayDisabled()) return;

      if (!this.overlayRef().hasAttached()) {
        this.overlayRef().attach(this.portal());
      } else if (this.overlayRef().hasAttached()) {
        this.overlayRef().detach();
      }
    }
  }

  private refreshConfig(
    partnerId?: string,
    brandPath?: string,
    marketId?: string,
    accountId?: string,
    serviceProviderId?: string,
    defaultURL?: string,
    entitlementId?: string,
  ): void {
    this.cfgService.initialize(partnerId, marketId, accountId, brandPath, serviceProviderId, defaultURL, entitlementId);
  }

  private calcAlwaysShowMenuToggle(): void {
    this.sidebarService.setShowMenuToggle(this.alwaysShowMenuToggle === true);
  }

  protected trackBy(i: SideNavigationItem): string {
    const key =
      i.sideNavigationLink?.navigationId ||
      i.sideNavigationLink?.translationId ||
      i.sideNavigationContainer?.navigationId ||
      i.sideNavigationContainer?.translationId ||
      i.sideNavigationSection?.translationId;

    return key;
  }
}

// Wires up logout behavior on the user-dropdown sign-out item.
// Native uses the oauth/logout route. On web, the cached session must be cleared before
// navigating to the server logout; otherwise the stale local_session JWT survives and the
// public sign-up route's PublicOnlyGuard treats the user as still authenticated, signing them
// back in (WOW-2391).
export function decorateSignOutItem(
  item: IdentifiableCustomUserDropdownItem,
  isNativePlatform: boolean,
  clearSession: () => void,
  navigate: (url: string) => void,
  consume: (url: string) => boolean = () => false,
): void {
  if (item.id !== signOutLabel) return;
  if (isNativePlatform) {
    item.url = '/oauth/logout';
    return;
  }
  const logoutUrl = item.url;
  item.callback = () => {
    if (consume(logoutUrl)) return;
    clearSession();
    navigate(logoutUrl);
  };
}

export function computeCanShowContactCard(
  description: string,
  hasSalesperson: boolean,
  hideSalesperson: boolean,
  canAccessInbox: boolean,
  showSupport: boolean,
  supportUrl: string,
): boolean {
  return (
    !!description.trim() || (hasSalesperson && !hideSalesperson) || canAccessInbox || (showSupport && !!supportUrl)
  );
}

export function removeUnpinnedItems(items: SideNavigationItem[], pins: PinnedItem[]): SideNavigationItem[] {
  return items.filter((item) => {
    if (!(item.sideNavigationLink || item.sideNavigationContainer)) return true;
    const relevantItem = item.sideNavigationLink || item.sideNavigationContainer;
    return !relevantItem.pinnable || pins?.find((pin) => pin.navigationId === relevantItem.navigationId);
  });
}

function isWpHostingLink(link: SideNavigationLink): boolean {
  const url = (link.url || '').toLowerCase();
  const navId = (link.navigationId || '').toLowerCase();
  return url.includes('websiteprodashboard') || navId.includes('wp-hosting');
}

// The Business App origin is taken from a sibling link, so the rewritten item also carries an absolute url for hosts that do not route.
export function overrideWpHostingLink(
  items: SideNavigationItem[],
  agid: string | null,
  origin: string | null = businessAppOrigin(items),
): SideNavigationItem[] {
  if (!agid) return items;
  const path = `/account/location/${agid}/wp-hosting`;
  return items.map((item) => {
    if (item.sideNavigationLink && isWpHostingLink(item.sideNavigationLink)) {
      return {
        ...item,
        sideNavigationLink: {
          ...item.sideNavigationLink,
          serviceProviderId: BUSINESS_APP_SERVICE_PROVIDER_ID,
          path,
          url: origin ? origin + path : item.sideNavigationLink.url,
          external: false,
          openInNewTab: false,
        },
      };
    }
    if (item.sideNavigationContainer) {
      return {
        ...item,
        sideNavigationContainer: {
          ...item.sideNavigationContainer,
          sideNavigationItems: overrideWpHostingLink(item.sideNavigationContainer.sideNavigationItems, agid, origin),
        },
      };
    }
    return item;
  });
}

function businessAppOrigin(items: SideNavigationItem[]): string | null {
  const link = flattenNavigationLinks(items).find(
    (l) => l.serviceProviderId === BUSINESS_APP_SERVICE_PROVIDER_ID && !isWpHostingLink(l) && isAbsoluteHttpUrl(l.url),
  );
  return link ? new URL(link.url).origin : null;
}
